/**
 * 🚀 PERSISTENT MESSAGING CACHE SERVICE
 *
 * Multi-layer caching strategy for instant messaging experience:
 * 1. Memory Cache (instant) - Map-based, survives component re-renders
 * 2. localStorage (fast) - Survives page refresh, tab switches
 * 3. API (authoritative) - Backend source of truth
 *
 * Features:
 * - TTL-based expiration
 * - Automatic cache invalidation
 * - Background revalidation (stale-while-revalidate pattern)
 * - Storage quota management
 * - Version-based cache invalidation
 */

const CACHE_PREFIX = "ResearchPro_msg_";
const CACHE_VERSION = "v1";
const MAX_STORAGE_MB = 5; // Max localStorage usage for messaging

// TTL configuration (milliseconds)
const TTL_CONFIG = {
  conversations: 3 * 60 * 1000, // 3 minutes
  messages: 10 * 60 * 1000, // 10 minutes
  contacts: 5 * 60 * 1000, // 5 minutes
  unreadCount: 1 * 60 * 1000, // 1 minute
};

class MessagingCacheService {
  constructor() {
    this.memoryCache = new Map();
    this.pendingWrites = new Map(); // Debounce localStorage writes
    this.initialized = false;
  }

  // ============================================
  // KEY MANAGEMENT
  // ============================================

  /**
   * Generate a consistent cache key
   * @param {string} key - Base key
   * @returns {string} Full cache key with prefix and version
   */
  getKey(key) {
    return `${CACHE_PREFIX}${CACHE_VERSION}_${key}`;
  }

  /**
   * Parse a storage key to extract the base key
   * @param {string} fullKey - Full storage key
   * @returns {string|null} Base key or null if not a cache key
   */
  parseKey(fullKey) {
    const prefix = `${CACHE_PREFIX}${CACHE_VERSION}_`;
    if (fullKey && fullKey.startsWith(prefix)) {
      return fullKey.slice(prefix.length);
    }
    return null;
  }

  // ============================================
  // CORE CACHE OPERATIONS
  // ============================================

  /**
   * Get data from cache (Memory → localStorage)
   * @param {string} key - Cache key
   * @param {string} type - Cache type for TTL lookup
   * @returns {any|null} Cached data or null
   */
  get(key, type = "messages") {
    const fullKey = this.getKey(key);
    const ttl = TTL_CONFIG[type] || TTL_CONFIG.messages;

    // 1. Check memory cache first (instant)
    if (this.memoryCache.has(fullKey)) {
      const entry = this.memoryCache.get(fullKey);
      if (Date.now() - entry.timestamp < ttl) {
return entry.data;
      }
      // Expired - remove from memory
      this.memoryCache.delete(fullKey);
    }

    // 2. Check localStorage (fast, survives refresh)
    try {
      const stored = localStorage.getItem(fullKey);
      if (stored) {
        const entry = JSON.parse(stored);

        // Validate version
        if (entry.version !== CACHE_VERSION) {
          localStorage.removeItem(fullKey);
          return null;
        }

        if (Date.now() - entry.timestamp < ttl) {
          // Restore to memory cache for faster subsequent access
          this.memoryCache.set(fullKey, entry);
return entry.data;
        }

        // Expired - remove from localStorage
        localStorage.removeItem(fullKey);
      }
    } catch (error) { /* ignored */ }
return null;
  }

  /**
   * Save data to cache (both memory and localStorage)
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   * @param {string} type - Cache type for TTL lookup
   * @param {Object} options - Additional options
   */
  set(key, data, type = "messages", options = {}) {
    const fullKey = this.getKey(key);
    const { skipPersist = false, metadata = {} } = options;

    const entry = {
      data,
      timestamp: Date.now(),
      version: CACHE_VERSION,
      type,
      ...metadata,
    };

    // 1. Save to memory (always instant)
    this.memoryCache.set(fullKey, entry);

    // 2. Save to localStorage (debounced for performance)
    if (!skipPersist) {
      this.persistToStorage(fullKey, entry);
    }
}

  /**
   * Debounced persistence to localStorage
   * @param {string} fullKey - Full cache key
   * @param {Object} entry - Cache entry
   */
  persistToStorage(fullKey, entry) {
    // Cancel pending write for this key
    if (this.pendingWrites.has(fullKey)) {
      clearTimeout(this.pendingWrites.get(fullKey));
    }

    // Debounce writes by 100ms
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem(fullKey, JSON.stringify(entry));
        this.pendingWrites.delete(fullKey);
      } catch (error) {
// Storage might be full - clear old entries
        if (
          error.name === "QuotaExceededError" ||
          error.code === 22 ||
          error.code === 1014
        ) {
          this.clearOldEntries();
          // Retry once
          try {
            localStorage.setItem(fullKey, JSON.stringify(entry));
          } catch (retryError) { /* ignored */ }
        }
      }
    }, 100);

    this.pendingWrites.set(fullKey, timeoutId);
  }

  // ============================================
  // INVALIDATION METHODS
  // ============================================

  /**
   * Invalidate a specific cache entry
   * @param {string} key - Cache key to invalidate
   */
  invalidate(key) {
    const fullKey = this.getKey(key);
    this.memoryCache.delete(fullKey);

    try {
      localStorage.removeItem(fullKey);
    } catch (error) { /* ignored */ }
}

  /**
   * Invalidate cache entries matching a pattern
   * @param {string} pattern - Pattern to match (will be used in a RegExp)
   */
  invalidatePattern(pattern) {
    const regex = new RegExp(pattern);

    // Clear from memory cache
    for (const key of this.memoryCache.keys()) {
      if (regex.test(key)) {
        this.memoryCache.delete(key);
      }
    }

    // Clear from localStorage
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX) && regex.test(key)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (error) { /* ignored */ }
}

  /**
   * Invalidate all cache for a specific conversation
   * @param {string} conversationId - Conversation ID
   */
  invalidateConversation(conversationId) {
    this.invalidatePattern(`messages_${conversationId}`);
    this.invalidate(`conversation_${conversationId}`);
    // Also invalidate conversations list to refresh last message
    this.invalidate("conversations_page_1");
    this.invalidate("conversations_with_presence");
  }

  /**
   * Clear all messaging cache
   */
  clearAll() {
    // Clear memory cache
    this.memoryCache.clear();

    // Clear localStorage
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (error) { /* ignored */ }
}

  // ============================================
  // SPECIALIZED CACHE METHODS
  // ============================================

  /**
   * Get cached conversations
   * @returns {Object|null} Cached conversations data
   */
  getConversations() {
    return this.get("conversations_with_presence", "conversations");
  }

  /**
   * Cache conversations
   * @param {Object} data - Conversations data
   */
  setConversations(data) {
    this.set("conversations_with_presence", data, "conversations");
  }

  /**
   * Get cached messages for a conversation
   * @param {string} conversationId - Conversation ID
   * @param {string} cursor - Pagination cursor (null for initial page)
   * @returns {Object|null} Cached messages data
   */
  getMessages(conversationId, cursor = null) {
    const key = cursor
      ? `messages_${conversationId}_cursor_${cursor}`
      : `messages_${conversationId}_initial`;
    return this.get(key, "messages");
  }

  /**
   * Cache messages for a conversation
   * @param {string} conversationId - Conversation ID
   * @param {Object} data - Messages data with pagination info
   * @param {string} cursor - Pagination cursor used for this fetch
   */
  setMessages(conversationId, data, cursor = null) {
    const key = cursor
      ? `messages_${conversationId}_cursor_${cursor}`
      : `messages_${conversationId}_initial`;
    this.set(key, data, "messages");
  }

  /**
   * Get cached unread counts
   * @returns {Object|null} Cached unread count data
   */
  getUnreadCount() {
    return this.get("unread_count", "unreadCount");
  }

  /**
   * Cache unread counts
   * @param {Object} data - Unread count data
   */
  setUnreadCount(data) {
    this.set("unread_count", data, "unreadCount");
  }

  /**
   * Get cached contacts
   * @returns {Object|null} Cached contacts data
   */
  getContacts() {
    return this.get("available_contacts", "contacts");
  }

  /**
   * Cache contacts
   * @param {Object} data - Contacts data
   */
  setContacts(data) {
    this.set("available_contacts", data, "contacts");
  }

  // ============================================
  // MESSAGE-SPECIFIC OPERATIONS
  // ============================================

  /**
   * Add a new message to cached messages (for optimistic updates)
   * @param {string} conversationId - Conversation ID
   * @param {Object} message - Message to add
   */
  addMessage(conversationId, message) {
    const cached = this.getMessages(conversationId);
    if (!cached) return;

    // Check for duplicates
    const exists = cached.messages?.some(
      (m) => m._id === message._id || m.tempId === message._id
    );
    if (exists) return;

    const updated = {
      ...cached,
      messages: [...(cached.messages || []), message],
    };

    this.setMessages(conversationId, updated);
  }

  /**
   * Update a message in cache (for status updates, edits, etc.)
   * @param {string} conversationId - Conversation ID
   * @param {string} messageId - Message ID (or tempId)
   * @param {Object} updates - Updates to apply
   */
  updateMessage(conversationId, messageId, updates) {
    const cached = this.getMessages(conversationId);
    if (!cached || !cached.messages) return;

    const updated = {
      ...cached,
      messages: cached.messages.map((msg) =>
        msg._id === messageId || msg.tempId === messageId
          ? { ...msg, ...updates }
          : msg
      ),
    };

    this.setMessages(conversationId, updated);
  }

  /**
   * Update conversation's last message
   * @param {string} conversationId - Conversation ID
   * @param {Object} message - Last message data
   */
  updateConversationLastMessage(conversationId, message) {
    const cached = this.getConversations();
    if (!cached || !cached.conversations) return;

    const updated = {
      ...cached,
      conversations: cached.conversations.map((conv) =>
        conv._id === conversationId
          ? {
              ...conv,
              lastMessage: {
                content: message.content || "Message",
                createdAt: message.createdAt || new Date().toISOString(),
                sender: message.sender,
              },
              lastActivity: message.createdAt || new Date().toISOString(),
            }
          : conv
      ),
    };

    this.setConversations(updated);
  }

  // ============================================
  // STORAGE MANAGEMENT
  // ============================================

  /**
   * Clear old cache entries when storage is full
   * Removes oldest 30% of entries
   */
  clearOldEntries() {
    try {
      const entries = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
          try {
            const entry = JSON.parse(localStorage.getItem(key) || "{}");
            entries.push({ key, timestamp: entry.timestamp || 0 });
          } catch (e) {
            // Invalid entry, mark for removal
            entries.push({ key, timestamp: 0 });
          }
        }
      }

      // Sort by timestamp (oldest first)
      entries.sort((a, b) => a.timestamp - b.timestamp);

      // Remove oldest 30%
      const toRemove = Math.max(1, Math.ceil(entries.length * 0.3));
      for (let i = 0; i < toRemove; i++) {
        localStorage.removeItem(entries[i].key);
        // Also remove from memory cache
        this.memoryCache.delete(entries[i].key);
      }
} catch (error) { /* ignored */ }
  }

  /**
   * Get approximate storage usage for messaging cache
   * @returns {Object} Storage usage info
   */
  getStorageInfo() {
    let totalSize = 0;
    let entryCount = 0;

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
          const value = localStorage.getItem(key);
          if (value) {
            totalSize += key.length + value.length;
            entryCount++;
          }
        }
      }
    } catch (error) { /* ignored */ }

    return {
      entries: entryCount,
      sizeBytes: totalSize * 2, // UTF-16 = 2 bytes per char
      sizeMB: ((totalSize * 2) / (1024 * 1024)).toFixed(2),
      maxMB: MAX_STORAGE_MB,
      percentUsed: (
        ((totalSize * 2) / (MAX_STORAGE_MB * 1024 * 1024)) *
        100
      ).toFixed(1),
    };
  }

  // ============================================
  // HYDRATION (For app startup)
  // ============================================

  /**
   * Hydrate memory cache from localStorage on app startup
   * This allows instant data display before API calls complete
   * @returns {Object} Hydrated data summary
   */
  hydrate() {
    if (this.initialized) {
      return { alreadyInitialized: true };
    }

    const hydrated = {
      conversations: false,
      unreadCount: false,
      messagesCount: 0,
    };

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
          try {
            const stored = localStorage.getItem(key);
            if (stored) {
              const entry = JSON.parse(stored);

              // Only hydrate if version matches and not expired
              if (entry.version === CACHE_VERSION) {
                const ttl = TTL_CONFIG[entry.type] || TTL_CONFIG.messages;
                if (Date.now() - entry.timestamp < ttl) {
                  this.memoryCache.set(key, entry);

                  // Track what was hydrated
                  if (key.includes("conversations"))
                    hydrated.conversations = true;
                  if (key.includes("unread_count")) hydrated.unreadCount = true;
                  if (key.includes("messages_")) hydrated.messagesCount++;
                }
              }
            }
          } catch (e) {
            // Skip invalid entries
          }
        }
      }
    } catch (error) { /* ignored */ }

    this.initialized = true;
return hydrated;
  }
}

// Singleton instance
const messagingCacheService = new MessagingCacheService();

export default messagingCacheService;
